import type { Campo } from './campos'

/**
 * Las plantillas del catálogo (CLAUDE.md §9): van en código, como los bloques, y
 * **se copian** al crear el formulario. A partir de ahí el formulario es del
 * cliente: cambiar la plantilla mañana no le toca el suyo.
 */
export type Plantilla = { slug: string; nombre: string; descripcion: string; campos: Campo[] }

const consentimiento = (): Campo => ({
  tipo: 'consentimiento',
  clave: 'consentimiento',
  etiqueta: 'Protección de datos',
  obligatorio: true,
  textoDelConsentimiento: 'He leído y acepto la política de privacidad, y autorizo el tratamiento de mis datos para responder a esta solicitud.',
})

const ocultos = (): Campo[] => [
  { tipo: 'oculto', clave: 'pagina', etiqueta: 'Página de origen', origen: 'pagina' },
  { tipo: 'oculto', clave: 'utm_source', etiqueta: 'Campaña (origen)', origen: 'utm_source' },
]

export const PLANTILLAS: Plantilla[] = [
  {
    slug: 'contacto',
    nombre: 'Contacto',
    descripcion: 'Nombre, correo y mensaje. El formulario que tiene todo el mundo.',
    campos: [
      { tipo: 'texto', clave: 'nombre', etiqueta: 'Nombre', obligatorio: true, ancho: 'mitad' },
      { tipo: 'email', clave: 'email', etiqueta: 'Correo electrónico', obligatorio: true, ancho: 'mitad' },
      { tipo: 'telefono', clave: 'telefono', etiqueta: 'Teléfono' },
      { tipo: 'area', clave: 'mensaje', etiqueta: 'Mensaje', obligatorio: true, minimoDeCaracteres: 10 },
      consentimiento(),
      ...ocultos(),
    ],
  },
  {
    slug: 'presupuesto',
    nombre: 'Presupuesto',
    descripcion: 'Dos pasos, con cantidades y un total que se calcula solo.',
    campos: [
      { tipo: 'texto', clave: 'nombre', etiqueta: 'Nombre o empresa', obligatorio: true, ancho: 'mitad' },
      { tipo: 'email', clave: 'email', etiqueta: 'Correo electrónico', obligatorio: true, ancho: 'mitad' },
      { tipo: 'paso', clave: 'paso_2', etiqueta: 'Qué necesitas' },
      {
        tipo: 'desplegable',
        clave: 'servicio',
        etiqueta: 'Servicio',
        obligatorio: true,
        opciones: [
          { etiqueta: 'Instalación', valor: 'instalacion' },
          { etiqueta: 'Mantenimiento', valor: 'mantenimiento' },
          { etiqueta: 'Otro', valor: 'otro' },
        ],
      },
      { tipo: 'area', clave: 'detalle', etiqueta: 'Cuéntanos qué necesitas', condicion: { modo: 'todas', reglas: [{ campo: 'servicio', operador: 'igual', valor: 'otro' }] } },
      { tipo: 'numero', clave: 'unidades', etiqueta: 'Unidades', obligatorio: true, minimo: 1, maximo: 9999, ancho: 'mitad' },
      { tipo: 'numero', clave: 'precioUnitario', etiqueta: 'Precio por unidad (referencia)', minimo: 0, ancho: 'mitad' },
      { tipo: 'calculado', clave: 'total', etiqueta: 'Total estimado', formula: 'unidades * precioUnitario' },
      consentimiento(),
      ...ocultos(),
    ],
  },
  {
    slug: 'cita',
    nombre: 'Reserva de cita',
    descripcion: 'Día y hora, con los datos de contacto.',
    campos: [
      { tipo: 'texto', clave: 'nombre', etiqueta: 'Nombre', obligatorio: true },
      { tipo: 'telefono', clave: 'telefono', etiqueta: 'Teléfono', obligatorio: true, ancho: 'mitad' },
      { tipo: 'email', clave: 'email', etiqueta: 'Correo electrónico', ancho: 'mitad' },
      { tipo: 'fecha', clave: 'dia', etiqueta: 'Día', obligatorio: true, ancho: 'mitad' },
      { tipo: 'hora', clave: 'hora', etiqueta: 'Hora', obligatorio: true, ancho: 'mitad' },
      { tipo: 'area', clave: 'motivo', etiqueta: 'Motivo de la visita' },
      consentimiento(),
      ...ocultos(),
    ],
  },
  {
    slug: 'encuesta',
    nombre: 'Encuesta',
    descripcion: 'Una valoración, si nos recomendarías y un comentario.',
    campos: [
      { tipo: 'valoracion', clave: 'valoracion', etiqueta: '¿Cómo ha ido?', obligatorio: true, minimo: 1, maximo: 5 },
      { tipo: 'escala', clave: 'recomendacion', etiqueta: '¿Qué probabilidad hay de que nos recomiendes?', minimo: 0, maximo: 10, etiquetaMinima: 'Nada probable', etiquetaMaxima: 'Muy probable' },
      { tipo: 'opcion-multiple', clave: 'volveria', etiqueta: '¿Volverías?', opciones: [{ etiqueta: 'Sí', valor: 'si' }, { etiqueta: 'No', valor: 'no' }] },
      { tipo: 'area', clave: 'comentario', etiqueta: '¿Algo que mejorar?' },
      ...ocultos(),
    ],
  },
  {
    slug: 'boletin',
    nombre: 'Alta en el boletín',
    descripcion: 'Un correo y el consentimiento. Nada más.',
    campos: [
      { tipo: 'email', clave: 'email', etiqueta: 'Correo electrónico', obligatorio: true },
      { tipo: 'consentimiento', clave: 'consentimiento', etiqueta: 'Consentimiento', obligatorio: true, textoDelConsentimiento: 'Quiero recibir novedades por correo y sé que puedo darme de baja cuando quiera.' },
      ...ocultos(),
    ],
  },
  {
    slug: 'informacion',
    nombre: 'Solicitud de información',
    descripcion: 'Con archivo adjunto, para pliegos y documentación.',
    campos: [
      { tipo: 'texto', clave: 'nombre', etiqueta: 'Nombre', obligatorio: true, ancho: 'mitad' },
      { tipo: 'email', clave: 'email', etiqueta: 'Correo electrónico', obligatorio: true, ancho: 'mitad' },
      { tipo: 'texto', clave: 'empresa', etiqueta: 'Empresa' },
      { tipo: 'area', clave: 'consulta', etiqueta: 'Qué necesitas saber', obligatorio: true },
      { tipo: 'archivo', clave: 'adjunto', etiqueta: 'Documento (opcional)', extensiones: ['pdf', 'jpg', 'png', 'docx'], maximoDeArchivos: 2 },
      consentimiento(),
      ...ocultos(),
    ],
  },
]

export const plantillaDe = (slug: string): Plantilla | undefined => PLANTILLAS.find((p) => p.slug === slug)
